import type { SettingsAreasUpdateRequest } from "@situate/contracts/runtime";
import {
  settingsAreasListResponseSchema,
  settingsAreasUpdateResponseSchema,
  settingsThresholdsListResponseSchema,
} from "@situate/contracts/runtime-zod";
import type {
  AdminSettingsConfiguration,
  ThresholdConfig,
  ThresholdUnit,
} from "@/features/deployment-settings/model/admin-settings";
import { getRuntimeHttpClientSnapshot } from "@/features/runtime-session/api/runtime-http-client";

const SETTINGS_AREAS_PATH = "/v1/settings/areas";

function getRuntimeClient() {
  const { client } = getRuntimeHttpClientSnapshot();
  if (client === null) {
    throw new Error("Runtime API client is unavailable");
  }

  return client;
}

function toAdminSettings(
  areasResponse: ReturnType<typeof settingsAreasListResponseSchema.parse>,
  thresholdsResponse: ReturnType<typeof settingsThresholdsListResponseSchema.parse>
): AdminSettingsConfiguration {
  return {
    thresholdConfigs: thresholdsResponse.threshold_configs.map(toThresholdConfig),
    areas: areasResponse.areas.map((area) => ({
      id: area.area_id,
      name: area.area_display_name,
      capacity: area.capacity?.max_people ?? "",
      maxDensityPeoplePerSquareMeter: area.capacity?.max_density_people_per_square_meter ?? null,
      capacityNotes: area.capacity?.notes ?? null,
    })),
  };
}

export async function fetchAdminSettings(
  _deploymentId: string
): Promise<AdminSettingsConfiguration | null> {
  const client = getRuntimeClient();
  const [areasResponse, thresholdsResponse] = await Promise.all([
    client.get<unknown>(SETTINGS_AREAS_PATH),
    client.get<unknown>("/v1/settings/thresholds"),
  ]);

  return toAdminSettings(
    settingsAreasListResponseSchema.parse(areasResponse),
    settingsThresholdsListResponseSchema.parse(thresholdsResponse)
  );
}

export async function saveAdminSettings(
  _deploymentId: string,
  configuration: AdminSettingsConfiguration
): Promise<AdminSettingsConfiguration> {
  const request: SettingsAreasUpdateRequest = {
    areas: configuration.areas.map((area) => ({
      area_id: area.id,
      capacity:
        area.capacity === ""
          ? null
          : {
              max_people: area.capacity,
              max_density_people_per_square_meter: area.maxDensityPeoplePerSquareMeter ?? null,
              notes: area.capacityNotes ?? null,
            },
    })),
  };
  const response = settingsAreasUpdateResponseSchema.parse(
    await getRuntimeClient().put<unknown>(SETTINGS_AREAS_PATH, request)
  );

  const thresholdsResponse = await getRuntimeClient().get<unknown>("/v1/settings/thresholds");
  return toAdminSettings(
    settingsAreasListResponseSchema.parse(response),
    settingsThresholdsListResponseSchema.parse(thresholdsResponse)
  );
}

function toThresholdConfig(
  config: ReturnType<typeof settingsThresholdsListResponseSchema.parse>["threshold_configs"][number]
): ThresholdConfig {
  const availableUnits = Array.from(
    new Set(
      config.thresholds.map((threshold) =>
        getThresholdUnit(config.metric.name, threshold.value_mode)
      )
    )
  );

  return {
    id: config.threshold_config_id,
    name: humanize(config.threshold_config_id),
    scopeLabel: config.scope.area_id ?? config.scope.queue_id ?? "Unknown scope",
    metricLabel: humanize(config.metric.name),
    availableUnits,
    availableSeverities: config.thresholds.map((threshold) => threshold.severity),
    thresholds: config.thresholds.map((threshold) => ({
      severity: threshold.severity,
      value: threshold.value,
      unit: getThresholdUnit(config.metric.name, threshold.value_mode),
    })),
  };
}

function getThresholdUnit(
  metricName: string,
  valueMode: "absolute" | "percent_of_capacity"
): ThresholdUnit {
  if (valueMode === "percent_of_capacity") return "percent_of_capacity";
  return metricName.includes("wait") || metricName.includes("minute") ? "minutes" : "people";
}

function humanize(value: string): string {
  return value
    .replaceAll("-", " ")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}
